import React, { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import withForm from 'modules/common/hoc/withForm';
import Select from 'react-select';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import Decimal from 'decimal.js';
import { get } from 'session';
import { MAX_LIMIT } from 'modules/common/constants';
import { ucwords } from '../../helper';
import * as actions from '../../actions';
import * as c from '../../constant';

function ValidateBusinessActivityModal({
  formData,
  setFormData,
  getNecessaryFieldsList,
  necessaryFieldsList,
  closeUpdateModal,
  handleValidateBusinessActivity,
  businessActivityInfo,
  grossAmount,
  setGrossAmount,
  isLoading,
  validating,
  formDataQuantityMatrix,
  setFormDataQuantityMatrix,
}) {
  const filteredNecessaryFieldsList = necessaryFieldsList.filter(
    (item) => !item.quantity_matrix
  );
  const hasGrossAmount = filteredNecessaryFieldsList.some((item) =>
    _.get(item, 'multiplier', '').includes('gross_amount_')
  );
  const filteredQuantityMatrix = necessaryFieldsList.filter(
    (item) => item.quantity_matrix
  );
  const quantityMatrix = _.get(businessActivityInfo, 'attributes.details.quantity_matrix', {});
  const multiplierQuantityMatrix = Object.values(quantityMatrix ?? {})
    .flatMap(item => _.isArray(item) ? item : [])
    .reduce((acc, { multiplier, value }) => {
      if (multiplier && !Number.isNaN(value)) {
        acc[multiplier] = +value;
      }
      return acc;
    }, {});

  useEffect(() => {
    getNecessaryFieldsList({
      business_line_id: _.get(
        businessActivityInfo,
        'attributes.business_line_id'
      ),
      application_status_id: get('applicationStatusId') || 0,
    });
  }, []);

  useEffect(() => {
    setGrossAmount({
      gross_amount: _.get(businessActivityInfo, 'attributes.gross_amount'),
    });
  }, []);

  useEffect(() => {
    let keyValues = [];
    keyValues = Object.entries(
      _.get(businessActivityInfo, 'attributes.details') || ''
    ).map(([key, value]) => ({
      [key]: value.toString() || '',
    }));

    let finalObject = {};
    const badCallback = (a, i) => Object.assign(a, i);
    finalObject = keyValues.reduce(badCallback, {});

    setFormData({
      ...formData,
      ...finalObject,
      business_line_id: +_.get(
        businessActivityInfo,
        'attributes.business_line_id'
      ),
    });
  }, []);

  // select necessary field
  const handleOfficeTypeDropDownChange = (key) => (e) => {
    setFormData({
      ...formData,
      [key]: e ? e.value : '',
    });
  };

  const selectOptions = (list) => {
    const newList = [];

    list.map((data) => {
      return newList.push({
        label: ucwords(_.get(data, 'multiplier'))
          .replace(/_/g, ' ')
          .replace(/^(.)|\s+(.)/g, (x) => x.toUpperCase()),
        value: data.multiplier,
      });
    });
    return newList;
  };

  const handleOnChange = (e) => {
    const { name, value } = e;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (!_.isEmpty(businessActivityInfo)) {
      setFormDataQuantityMatrix({
        ...formDataQuantityMatrix,
        ...multiplierQuantityMatrix,
      });
    }
  }, []);

  const onChangeQuantityMatrix = (key, value) => {
    if (value === undefined || _.isNaN(value)) return;

    setFormDataQuantityMatrix(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    if (!formData) return;

    // If either field is missing, do nothing
    if (
      formData.gross_amount_essential === undefined ||
      formData.gross_amount_nonessential === undefined
    ) {
      return;
    }

    const essentials = parseFloat(formData.gross_amount_essential) || 0;
    const nonessentials = parseFloat(formData.gross_amount_nonessential) || 0;

    // If both are zero, do nothing
    if (essentials === 0 && nonessentials === 0) {
      return;
    }

    const total = essentials + nonessentials;

    setGrossAmount({
      ...grossAmount,
      gross_amount: total || _.get(grossAmount, 'gross_amount'),
    });

  }, [formData?.gross_amount_essential, formData?.gross_amount_nonessential]);

  return (
    <div className="p-3">
      {isLoading ? (
        <div className="loading">
          <div className="spinner text-center">
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        </div>
      ) : (
        <>
          <div className="">
            <h4 className="text-primary font-weight-bold mr-2">
              Business Line
            </h4>
            <b>{_.get(businessActivityInfo, 'attributes.name')}</b>
          </div>
          <div className="row mt-3">
            <div className="col-6">
              <div className="form-group">
                <label className="form-label">Gross Amount</label>
                <NumberFormat
                  name="gross_amount"
                  id="gross_amount"
                  placeholder="0.00"
                  className="form-control"
                  onValueChange={(values) => {
                    const { value } = values;
                    setGrossAmount({
                      ...grossAmount,
                      gross_amount: value,
                    });
                  }}
                  displayType="input"
                  decimalScale={2}
                  isNumericString
                  allowNegative
                  thousandSeparator
                  allowLeadingZeros
                  prefix="₱ "
                  disabled={hasGrossAmount}
                  value={_.get(grossAmount, 'gross_amount')}
                  isAllowed={(values) => {
                    const { value } = values;
                    if (!value) return true;
                    const val = new Decimal(value);
                    const max = new Decimal(MAX_LIMIT);
                    return value === undefined || val.lte(max);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="row">
            {filteredNecessaryFieldsList.map((item) => {
              const { quantity_matrix, ...detail } = item;
              const options = _.get(detail, 'options');
              const multiplier = _.get(detail, 'multiplier', '');
              const hasGrossKey = multiplier.includes('gross_amount_');

              return (
                <div key={`necessary-field-${multiplier}`} className="col-6">
                  <div className="form-group">
                    {options ? (
                      <div>
                        <label className="form-label">
                          {ucwords(multiplier.replace(/_/g, ' '))}{' '}
                        </label>
                        <Select
                          onChange={handleOfficeTypeDropDownChange(multiplier)}
                          options={selectOptions(options)}
                          value={
                            selectOptions(options).find(
                              (x) =>
                                _.get(formData, multiplier) ===
                                _.get(x, 'value')
                            ) || ''
                          }
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="form-label">
                          {ucwords(multiplier.replace(/_/g, ' '))}
                        </label>
                        <NumberFormat
                          name={multiplier}
                          id={multiplier}
                          placeholder="0.00"
                          className="form-control"
                          onValueChange={(values) => {
                            const { value } = values;
                            const prop = {
                              name: multiplier,
                              value,
                            };
                            handleOnChange(prop);
                          }}
                          displayType="input"
                          decimalScale={2}
                          isNumericString
                          allowNegative
                          thousandSeparator
                          allowLeadingZeros
                          required
                          prefix={hasGrossKey ? '₱ ' : ''}
                          value={_.get(formData, multiplier)}
                          isAllowed={(values) => {
                            const { value } = values;
                            if (!hasGrossKey || !value) return true;
                            const val = new Decimal(value);
                            const max = new Decimal(MAX_LIMIT);
                            return value === undefined || val.lte(max);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {!_.isEmpty(filteredQuantityMatrix) ? (
              filteredQuantityMatrix.map((item) => {
                const multiplier = _.get(item, 'multiplier', '');
                return (
                  <React.Fragment>
                    <div className='col-12 form-group'>
                      <label className='form-label text-capitalize'>
                        {multiplier || ''}
                      </label>
                      <div className="w-100 border rounded-lg ">
                        <table className="table table-borderless table-sm table-striped table-hover mb-0">
                          <thead>
                            <tr className="form-group">
                              <td className="col-4 text-center form-label border-right">Unit</td>
                              <td className="col-4 text-center form-label border-right">Capacity</td>
                              <td className="col-4 text-center form-label">Number of Units</td>
                            </tr>
                          </thead>
                          <tbody>
                            {item.quantity_matrix.map((quantityMatrixItem) => {
                              const quantityMatrixMultiplier = _.get(quantityMatrixItem, 'multiplier');

                              return (
                                <tr className="border-top form-group">
                                  <td className="text-left form-label border-right">
                                    {_.get(quantityMatrixItem, 'unit')}
                                  </td>
                                  <td className="text-left form-label border-right">
                                    {_.get(quantityMatrixItem, 'capacity')}
                                  </td>
                                  <td className="text-left form-label">
                                    <NumberFormat
                                      name={quantityMatrixMultiplier}
                                      id={quantityMatrixMultiplier}
                                      placeholder=""
                                      className="form-control"
                                      onValueChange={({ floatValue }) => {
                                        onChangeQuantityMatrix(quantityMatrixMultiplier, floatValue);
                                      }}
                                      displayType="input"
                                      thousandSeparator
                                      allowLeadingZeros
                                      isNumericString
                                      value={formDataQuantityMatrix[quantityMatrixMultiplier] ?? ''}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </React.Fragment>
                )
              })
            ) : (
              ''
            )}
          </div>
          <div className="d-flex justify-content-end pb-3">
            <button
              type="button"
              className="mt-3 btn btn-primary cms-bg-color-primary cms-outline-primary mr-3"
              onClick={handleValidateBusinessActivity(formData)}
            >
              <span className="font-weight-bold">
                {validating ? (
                  <i className="fa fa-spin fa-spinner" />
                ) : (
                  `Validate`
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() => closeUpdateModal()}
              className="mt-3 btn btn-dark"
            >
              <span className="font-weight-bold">Cancel</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

ValidateBusinessActivityModal.propTypes = {
  formData: PropTypes.instanceOf(Object).isRequired,
  setFormData: PropTypes.instanceOf(Object).isRequired,
  getNecessaryFieldsList: PropTypes.instanceOf(Function).isRequired,
  necessaryFieldsList: PropTypes.instanceOf(Array).isRequired,
  closeUpdateModal: PropTypes.instanceOf(Function).isRequired,
  handleValidateBusinessActivity: PropTypes.instanceOf(Function).isRequired,
  businessActivityInfo: PropTypes.instanceOf(Object).isRequired,
  grossAmount: PropTypes.instanceOf(Object).isRequired,
  setGrossAmount: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  validating: PropTypes.bool.isRequired,
  formDataQuantityMatrix: PropTypes.instanceOf(Object).isRequired,
  setFormDataQuantityMatrix: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  necessaryFieldsList: _.get(api, `${c.GET_NECESSARY_FIELD_LIST}.list`) || [],
  isLoading: api.loading.indexOf(c.GET_NECESSARY_FIELD_LIST) > -1,
  validating: api.loading.indexOf(c.VALIDATE_BUSINESS_ACTIVITY) > -1,
});

const enhance = _.flowRight([
  withForm,
  withRouter,
  connect(mapStateToProps, {
    ...actions,
  }),
]);

export default enhance(ValidateBusinessActivityModal);
